<script setup lang="ts">
import BackstageButton from '@/components/common/BackstageButton.vue';
import BackstageImage from '@/components/common/BackstageImage.vue';
import { ButtonVariant } from '@/models/button-variant.enum';
import { Icons } from '@/models/icons.enum';
import type { ProductModelDetails } from '@/types/matrix.interface';
import type { AllowableProductModelDto } from '@onrunning/backstage-models';
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
  productModel: AllowableProductModelDto | ProductModelDetails;
  hideSelectButton: boolean;
  isSelected: boolean;
}>();

const emit = defineEmits<{
  toggle: [];
  close: [];
}>();

const { t } = useI18n();
const selectedImageIndex = ref<number>(0);

const productModelImages = computed((): string[] => {
  if (!props.productModel?.defaultVariantImages) {
    return [];
  }

  return props.productModel.defaultVariantImages;
});

const getOrderActionText = computed(() => {
  return props.isSelected ? t('create_edit_order.remove_from_order') : t('create_edit_order.add_to_order');
});

const toggleProductModelSelection = async () => {
  await nextTick(() => {
    emit('toggle');
  });
};

const close = () => {
  emit('close');
};

const selectImage = (index: number) => {
  selectedImageIndex.value = index;
};
</script>

<template>
  <div class="view-product-details">
    <div class="view-product-details-cnt">
      <div class="product-details-top-bar" data-cy="top-bar">
        <div data-cy="product-model-name">{{ productModel?.name }}</div>
        <div class="product-model-action">
          <BackstageButton
            v-if="!hideSelectButton"
            :icon-right="props.isSelected ? Icons.SUBTRACT_SIGN : Icons.ADDITION_SIGN"
            @click="toggleProductModelSelection"
            data-cy="toggle-from-order-btn-cnt"
            data-cy-button="toggle-from-order-btn">
            {{ getOrderActionText }}
          </BackstageButton>
          <BackstageButton
            :variant="ButtonVariant.SECONDARY"
            icon-right="x-close"
            @click="close"
            data-cy="close-button-cnt"
            data-cy-button="close-button" />
        </div>
      </div>
      <div class="product-details-images">
        <div class="current-image" data-cy="current-image">
          <BackstageImage :src="productModelImages[selectedImageIndex]" alt="Selected Image" />
        </div>
        <div class="image-carousel" data-cy="image-carousel">
          <BackstageImage
            v-for="(image, index) of productModelImages"
            :key="index"
            class="image-carousel-item"
            :class="[{ selected: index === selectedImageIndex }]"
            @click="selectImage(index)"
            :src="`${image}?w=200`"
            alt="Selected Image" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.view-product-details {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  overflow-y: scroll;
  background-color: var(--bg-page-color);

  .view-product-details-cnt {
    @include common-container;
    padding: 20px;

    .product-details-top-bar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 20px;
      box-shadow:
        0px 0px 3px var(--shadow-color),
        0px 12px 24px var(--shadow-color);
      border-radius: 8px;
      background-color: var(--primary-bg-color);

      p {
        margin: 0;
      }

      .product-model-action {
        display: flex;
        align-items: center;
        gap: 16px;

        button {
          margin-right: 20px;

          &:last-child {
            margin: 0;
          }

          &.btn-close {
            position: relative;
            width: 42px;

            .ico-close {
              height: 24px;
              width: 24px;
              display: block;
              left: calc(50% - 11.5px);
              position: absolute;
              top: calc(50% - 11.5px);
            }
          }
        }
      }
    }

    .product-details-images {
      height: calc(100vh - 114px);
      display: flex;
      flex-direction: column;

      .current-image {
        width: 100%;
        padding: 10px 0;
        height: 77%;

        img {
          max-width: 100%;
          height: 100%;
        }
      }

      .image-carousel {
        margin: 0;
        display: flex;
        flex-wrap: wrap;
        gap: 20px;
        justify-content: flex-end;
        padding-bottom: 2rem;

        @include breakpoint(medium) {
          justify-content: center;
        }

        .image-carousel-item {
          height: 13rem;
          cursor: pointer;
          aspect-ratio: 3 / 2;
          object-fit: contain;
          border-radius: 4px;
          box-shadow:
            0px 0px 4px var(--shadow-color),
            0px 16px 32px var(--shadow-color);

          &.selected {
            outline: 2px solid var(--new-primary-color);
          }
        }
      }
    }
  }
}
</style>
